// =============================================================================
// Library Taxonomy Page — tabbed: category browser (tree + preview) + tag table
// =============================================================================
// Categories tab (PR-C two-column browser): searchable, collapsible tree on
// the left; selecting a node shows a thumbnail preview of its assets (via the
// extended category filter — direct members + linked-tag members) on the
// right with an "Open category" action to the detail page. Tags tab:
// searchable table with per-row rename/delete + inline add.
// RBAC: structural ops (category create/rename/delete, tag rename/delete)
// need library:admin; tag CREATE stays library:write (Combobox inline-create).
// =============================================================================

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Combobox,
  Input,
  Modal,
  ModalFooter,
  Spinner,
  Table,
  useConfirm,
} from '@tkpod/ui';
import type { Column } from '@tkpod/ui';
import type { LibraryCategoryNode, LibraryTagSummary } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';
import {
  createCategory,
  createTag,
  deleteCategory,
  deleteTag,
  fetchCategories,
  fetchTags,
  flattenCategories,
  libraryKeys,
  searchAssets,
  updateCategory,
  updateTag,
} from './api';

const PREVIEW_PAGE_SIZE = 12;

interface RenameTarget {
  kind: 'category' | 'tag';
  id: string;
  name: string;
}

/** Recursively filter the tree: keep nodes that match or have a matching
 * descendant (ancestors of matches remain so the path stays readable). */
function filterTree(nodes: LibraryCategoryNode[], q: string): LibraryCategoryNode[] {
  if (!q) return nodes;
  const needle = q.toLowerCase();
  const out: LibraryCategoryNode[] = [];
  for (const n of nodes) {
    const children = filterTree(n.children ?? [], q);
    if (n.name.toLowerCase().includes(needle) || children.length) {
      out.push({ ...n, children });
    }
  }
  return out;
}

function countNodes(nodes: LibraryCategoryNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + countNodes(n.children ?? []), 0);
}

function findNode(nodes: LibraryCategoryNode[], id: string): LibraryCategoryNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = findNode(n.children ?? [], id);
    if (hit) return hit;
  }
  return null;
}

function CategoryTree({
  nodes,
  depth,
  collapsed,
  selectedId,
  canAdmin,
  onToggle,
  onSelect,
  onDelete,
  onRename,
  forceExpand,
}: {
  nodes: LibraryCategoryNode[];
  depth: number;
  collapsed: Set<string>;
  selectedId: string | null;
  canAdmin: boolean;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onDelete: (n: LibraryCategoryNode) => void;
  onRename: (n: LibraryCategoryNode) => void;
  /** Search active → every branch containing a match stays open. */
  forceExpand: boolean;
}) {
  return (
    <div className={depth > 0 ? 'ml-4 border-l border-brand-900 pl-4' : ''}>
      {nodes.map((n) => {
        const hasChildren = Boolean(n.children?.length);
        const isCollapsed = !forceExpand && collapsed.has(n.id);
        const isSelected = selectedId === n.id;
        return (
          <div key={n.id}>
            <div
              className={`group flex items-center justify-between rounded px-1 py-1.5 ${
                isSelected ? 'bg-brand-950' : ''
              }`}
            >
              {/* Selected row uses a dark (brand-950) background — every text
                  token inside must flip to white or it goes dark-on-dark. */}
              <span
                className={`flex min-w-0 items-center gap-1.5 text-sm ${
                  isSelected ? 'text-white' : 'text-text-primary'
                }`}
              >
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => onToggle(n.id)}
                    aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                    className={`flex h-4 w-4 shrink-0 items-center justify-center ${
                      isSelected
                        ? 'text-white/70 hover:text-white'
                        : 'text-text-tertiary hover:text-text-primary'
                    }`}
                  >
                    <svg
                      className={`h-3 w-3 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                ) : (
                  <span className="h-4 w-4 shrink-0" />
                )}
                <button
                  type="button"
                  onClick={() => onSelect(n.id)}
                  className={`truncate text-left ${
                    isSelected ? 'font-medium text-white' : 'hover:text-brand-400'
                  }`}
                >
                  {n.name}
                </button>
                <span
                  className={`shrink-0 text-xs ${
                    isSelected ? 'text-white/60' : 'text-text-tertiary'
                  }`}
                >
                  ({n.assetCount ?? 0})
                </span>
                {n.linkedTagCount ? (
                  <Badge variant="primary" size="sm">{n.linkedTagCount} tag{n.linkedTagCount !== 1 ? 's' : ''}</Badge>
                ) : null}
                {!n.galleryVisible && (
                  <Badge variant="warning" size="sm">hidden</Badge>
                )}
              </span>
              {canAdmin && (
                <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={isSelected ? '!text-white/85 hover:!bg-white/10 hover:!text-white' : undefined}
                    onClick={() => onRename(n)}
                  >
                    Rename
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={`!text-red-400 ${isSelected ? 'hover:!bg-white/10' : ''}`}
                    onClick={() => onDelete(n)}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
            {hasChildren && !isCollapsed ? (
              <CategoryTree
                nodes={n.children!}
                depth={depth + 1}
                collapsed={collapsed}
                selectedId={selectedId}
                canAdmin={canAdmin}
                onToggle={onToggle}
                onSelect={onSelect}
                onDelete={onDelete}
                onRename={onRename}
                forceExpand={forceExpand}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function LibraryTaxonomyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { user } = useAuth();
  const canAdmin = (user?.permissions ?? []).includes('library:admin');

  const categoriesQuery = useQuery({ queryKey: libraryKeys.categories, queryFn: fetchCategories });
  const tagsQuery = useQuery({ queryKey: libraryKeys.tags, queryFn: fetchTags });

  const [tab, setTab] = useState<'categories' | 'tags'>('categories');
  const [catSearch, setCatSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  const [catName, setCatName] = useState('');
  const [catParent, setCatParent] = useState<string | null>(null);
  const [tagName, setTagName] = useState('');
  const [rename, setRename] = useState<RenameTarget | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const parentOptions = useMemo(
    () => flattenCategories(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );
  const visibleTree = useMemo(
    () => filterTree(categoriesQuery.data ?? [], catSearch.trim()),
    [categoriesQuery.data, catSearch],
  );
  const totalCategories = useMemo(
    () => countNodes(categoriesQuery.data ?? []),
    [categoriesQuery.data],
  );
  const selectedNode = useMemo(
    () => (selectedCatId ? findNode(categoriesQuery.data ?? [], selectedCatId) : null),
    [categoriesQuery.data, selectedCatId],
  );
  const visibleTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    const all = tagsQuery.data ?? [];
    return q ? all.filter((t) => t.name.toLowerCase().includes(q)) : all;
  }, [tagsQuery.data, tagSearch]);

  // Preview grid for the selected category — uses the EXTENDED category
  // filter (direct members + linked-tag members), same as the detail page.
  const previewQuery = useQuery({
    queryKey: libraryKeys.assets({ preview: selectedCatId, pageSize: PREVIEW_PAGE_SIZE }),
    queryFn: () =>
      searchAssets(
        new URLSearchParams({
          categoryId: selectedCatId!,
          page: '1',
          pageSize: String(PREVIEW_PAGE_SIZE),
        }).toString(),
      ),
    enabled: !!selectedCatId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['library'] });

  const createCatMut = useMutation({
    mutationFn: () => createCategory({ name: catName.trim(), parentId: catParent || undefined }),
    onSuccess: () => { toast('Category created', 'success'); setCatName(''); setCatParent(null); void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const deleteCatMut = useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: (res, id) => {
      toast(
        res.unlinkedTags
          ? `Category deleted (${res.unlinkedTags} linked tag(s) unlinked; assets keep their tags)`
          : 'Category deleted',
        'success',
      );
      if (selectedCatId === id) setSelectedCatId(null);
      void invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const createTagMut = useMutation({
    mutationFn: () => createTag({ name: tagName.trim() }),
    onSuccess: () => { toast('Tag created', 'success'); setTagName(''); void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const deleteTagMut = useMutation({
    mutationFn: (id: string) => deleteTag(id),
    onSuccess: (res) => {
      toast(
        `Tag deleted (removed from ${res.removedFrom} asset(s)${res.unlinkedCategories ? `, unlinked from ${res.unlinkedCategories} categor${res.unlinkedCategories === 1 ? 'y' : 'ies'}` : ''})`,
        'success',
      );
      void invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const renameMut = useMutation({
    mutationFn: async (): Promise<unknown> =>
      rename!.kind === 'category'
        ? updateCategory(rename!.id, { name: renameValue.trim() })
        : updateTag(rename!.id, { name: renameValue.trim() }),
    onSuccess: () => { toast('Renamed', 'success'); setRename(null); void invalidate(); },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const openRename = (target: RenameTarget) => {
    setRename(target);
    setRenameValue(target.name);
  };

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onDeleteCategory = async (n: LibraryCategoryNode) => {
    // Richer confirm (PR-C): linked tags never block a delete — they are
    // unlinked (assets keep their tags). Direct assets/children still 409.
    const tagNote = n.linkedTagCount
      ? ` ${n.linkedTagCount} linked tag(s) will be unlinked; assets keep their tags.`
      : '';
    const ok = await confirm({
      title: 'Delete category',
      description: `Delete "${n.name}"? It must have no assets or sub-categories.${tagNote}`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) deleteCatMut.mutate(n.id);
  };
  const onDeleteTag = async (t: LibraryTagSummary) => {
    const ok = await confirm({
      title: 'Delete tag',
      description: `Delete "${t.name}"? It is used by ${t.assetCount ?? 0} asset(s) and linked to ${t.categoryCount ?? 0} categor${(t.categoryCount ?? 0) === 1 ? 'y' : 'ies'}.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) deleteTagMut.mutate(t.id);
  };

  const tagColumns: Column<LibraryTagSummary>[] = [
    { key: 'name', header: 'Tag', render: (t) => <span className="font-medium text-text-primary">{t.name}</span> },
    { key: 'slug', header: 'Slug', render: (t) => <span className="text-text-tertiary">{t.slug}</span> },
    { key: 'assetCount', header: 'Assets', render: (t) => <span className="text-text-secondary">{t.assetCount ?? 0}</span> },
    { key: 'categoryCount', header: 'Categories', render: (t) => <span className="text-text-secondary">{t.categoryCount ?? 0}</span> },
    ...(canAdmin
      ? [
          {
            key: 'actions',
            header: '',
            className: 'text-right',
            render: (t: LibraryTagSummary) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" onClick={() => openRename({ kind: 'tag', id: t.id, name: t.name })}>Rename</Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="!text-red-400"
                  loading={deleteTagMut.isPending && deleteTagMut.variables === t.id}
                  onClick={() => void onDeleteTag(t)}
                >
                  Delete
                </Button>
              </div>
            ),
          } satisfies Column<LibraryTagSummary>,
        ]
      : []),
  ];

  const previewItems = previewQuery.data?.items ?? [];
  const previewTotal = previewQuery.data?.total ?? 0;

  return (
    <div className="space-y-6">
      {dialog}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-brand-900">
        {(
          [
            { id: 'categories', label: `Categories (${totalCategories})` },
            { id: 'tags', label: `Tags (${tagsQuery.data?.length ?? 0})` },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-brand-500 text-text-primary'
                : 'border-transparent text-text-tertiary hover:text-text-primary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'categories' && (
        <div className="space-y-4">
          {/* Add form — structural op, library:admin only */}
          {canAdmin && (
            <Card>
              <CardHeader
                title="Add category"
                description="Create a new category, optionally nested under a parent."
              />
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="w-64">
                  <span className="mb-1.5 block text-sm font-medium text-text-primary">Name</span>
                  <Input
                    placeholder="Category name"
                    value={catName}
                    onChange={(e) => setCatName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && catName.trim() && createCatMut.mutate()}
                  />
                </div>
                <div className="w-64">
                  <Combobox
                    label="Parent"
                    options={parentOptions}
                    value={catParent}
                    onChange={setCatParent}
                    placeholder="No parent (root)"
                    searchPlaceholder="Search categories…"
                  />
                </div>
                <Button loading={createCatMut.isPending} disabled={!catName.trim()} onClick={() => createCatMut.mutate()}>Add</Button>
              </div>
            </Card>
          )}

          {/* Two-column browser: tree left, selected-category preview right */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="Category tree" description="Select a category to preview its assets." />
              <div className="mt-4">
                <Input
                  placeholder="Search categories…"
                  value={catSearch}
                  onChange={(e) => setCatSearch(e.target.value)}
                />
              </div>

              <div className="mt-4">
                {categoriesQuery.isLoading ? (
                  <Spinner size="md" label="Loading…" />
                ) : (categoriesQuery.data?.length ?? 0) === 0 ? (
                  <p className="text-sm text-text-tertiary">No categories yet.</p>
                ) : visibleTree.length === 0 ? (
                  <p className="text-sm text-text-tertiary">No categories match “{catSearch.trim()}”.</p>
                ) : (
                  <CategoryTree
                    nodes={visibleTree}
                    depth={0}
                    collapsed={collapsed}
                    selectedId={selectedCatId}
                    canAdmin={canAdmin}
                    onToggle={toggleCollapse}
                    onSelect={setSelectedCatId}
                    onDelete={(n) => void onDeleteCategory(n)}
                    onRename={(n) => openRename({ kind: 'category', id: n.id, name: n.name })}
                    forceExpand={catSearch.trim().length > 0}
                  />
                )}
              </div>
            </Card>

            <Card>
              {!selectedNode ? (
                <div className="flex h-full min-h-48 items-center justify-center">
                  <p className="text-sm text-text-tertiary">
                    Select a category on the left to preview its assets.
                  </p>
                </div>
              ) : (
                <>
                  <CardHeader
                    title={selectedNode.name}
                    description={`${previewTotal} asset(s) incl. linked-tag members · ${selectedNode.linkedTagCount ?? 0} linked tag(s)${selectedNode.galleryVisible ? '' : ' · hidden from gallery'}`}
                    action={
                      <Button
                        size="sm"
                        onClick={() => navigate(`/library/categories/${selectedNode.id}`)}
                      >
                        Open category
                      </Button>
                    }
                  />
                  <div className="mt-4">
                    {previewQuery.isLoading ? (
                      <div className="flex justify-center py-10">
                        <Spinner size="md" label="Loading preview…" />
                      </div>
                    ) : previewItems.length === 0 ? (
                      <p className="py-8 text-center text-sm text-text-tertiary">
                        No assets in this category yet.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {previewItems.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => navigate(`/library/assets/${a.id}`)}
                            className="group overflow-hidden rounded border border-brand-900 bg-surface-secondary"
                            title={a.viaTagName ? `${a.sku} (via tag: ${a.viaTagName})` : a.sku}
                          >
                            <div className="flex aspect-square items-center justify-center bg-[#bdbdbd] p-1">
                              {a.thumbUrl && (
                                <img
                                  src={a.thumbUrl}
                                  alt={a.sku}
                                  className="max-h-full max-w-full object-contain"
                                />
                              )}
                            </div>
                            <p className="truncate px-1.5 py-1 text-left text-[11px] text-text-secondary group-hover:text-text-primary">
                              {a.sku}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                    {previewTotal > PREVIEW_PAGE_SIZE && (
                      <p className="mt-2 text-center text-xs text-text-tertiary">
                        Showing the first {PREVIEW_PAGE_SIZE} — open the category for the full list.
                      </p>
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      )}

      {tab === 'tags' && (
        <Card>
          <CardHeader title="Tags" description="Flat tags applied to assets. Linking a tag to a category (on the category page) lists its assets there automatically." />
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Input
              placeholder="Tag name"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && tagName.trim() && createTagMut.mutate()}
              className="max-w-xs"
            />
            <Button size="sm" loading={createTagMut.isPending} disabled={!tagName.trim()} onClick={() => createTagMut.mutate()}>Add</Button>
            <div className="ml-auto w-64">
              <Input
                placeholder="Search tags…"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4">
            {tagsQuery.isLoading ? (
              <Spinner size="md" label="Loading…" />
            ) : (
              <Table<LibraryTagSummary>
                columns={tagColumns}
                data={visibleTags}
                rowKey={(t) => t.id}
                emptyMessage={
                  tagSearch.trim() ? `No tags match “${tagSearch.trim()}”.` : 'No tags yet.'
                }
              />
            )}
          </div>
        </Card>
      )}

      <Modal
        open={!!rename}
        onClose={() => setRename(null)}
        title={rename?.kind === 'category' ? 'Rename category' : 'Rename tag'}
        size="sm"
      >
        <Input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder="New name"
        />
        <ModalFooter>
          <Button variant="ghost" onClick={() => setRename(null)}>Cancel</Button>
          <Button
            loading={renameMut.isPending}
            disabled={!renameValue.trim()}
            onClick={() => renameMut.mutate()}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
